import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, BadgeCheck, FileText } from "lucide-react";
import { motion } from "framer-motion";
import {
  FaShieldAlt, // For Zero Depreciation
  FaTools, // For Engine Protection
  FaFileInvoiceDollar, // For RTI
  FaTruckPickup, // For Roadside Assistance
  FaCarSide, // For Tyre and Rim Cover
  FaCloudSunRain,
  FaUserLock,
  FaCarCrash,
} from "react-icons/fa";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${
        id === open ? "rotate-180" : ""
      } h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const TataCarInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Is zero depreciation useful for Tata Nexon or Safari?",
      answer:
        "Yes, especially in the first 3 to 5 years when repairs and parts replacement can be costly.",
    },
    {
      question: "How is Tata car insurance premium calculated?",
      answer:
        "Premiums are based on IDV, vehicle model, registration city, add-ons and past claim history.",
    },
    {
      question: "Can I buy Tata car insurance online?",
      answer:
        "Yes, most insurers offer an easy online purchase and renewal process.",
    },
    {
      question: "Which Tata car is cheapest to insure?",
      answer:
        "The Tiago and Tigor models usually have the lowest insurance premiums.",
    },
    {
      question: "Is it possible to renew expired Tata insurance digitally?",
      answer:
        "Yes, but inspections may be required if the policy has lapsed beyond a few weeks.",
    },
    {
      question: "Does diesel Harrier cost more to insure than petrol variants?",
      answer:
        "Yes, diesel engines typically lead to higher premiums due to repair costs.",
    },
    {
      question: "What happens to insurance if I sell my Tata vehicle?",
      answer:
        "The insurance can be transferred to the buyer or cancelled with refund for unused days.",
    },
    {
      question: "Are electric Tata models like Nexon EV covered?",
      answer:
        "Yes, most insurers now offer policies that include EV-specific coverage like battery protection.",
    },
    {
      question: "Can I use my old car’s NCB when buying a new Tata?",
      answer:
        "Yes, as long as the car is registered under the same person, NCB is transferable.",
    },
    {
      question: "Is RTI useful for Tata Punch or Altroz?",
      answer:
        "RTI is recommended within the first few years for better protection in total loss or theft.",
    },
    {
      question:
        "Do modifications like alloy wheels or fog lamps affect insurance?",
      answer:
        "Yes. Inform the insurer and include accessories in the declared value.",
    },
    {
      question: "Are tyres covered under standard Tata insurance?",
      answer:
        "Only in accidental damage. Tyre protection add-on can extend this coverage.",
    },
    {
      question: "Does Tata sell its own car insurance?",
      answer:
        "Yes, via tie-ups with providers like Tata AIG, but always compare third-party quotes too.",
    },
    {
      question: "What is the penalty for driving without Tata insurance?",
      answer:
        "As per Indian Motor Law, fines range from ₹2,000 to ₹4,000 for repeated offences.",
    },
    {
      question: "What is covered under comprehensive Tata insurance?",
      answer:
        "It includes own damage, third-party liability, fire, theft, natural disasters and more.",
    },
    {
      question: "Can I change insurance provider mid-policy?",
      answer: "Not usually. Switching is best done at renewal time.",
    },
    {
      question: "Will I need a car inspection to switch insurers?",
      answer:
        "Only if there is a gap in policy or new add-ons are added post-expiry.",
    },
    {
      question: "Are there cashless garages for Tata across India?",
      answer:
        "Yes, most insurers provide access to 3,000 or more garages including Tata-authorized centers.",
    },
    {
      question: "How long does it take to settle a claim?",
      answer:
        "Claim processing usually takes 7 to 14 working days, depending on documents and verification.",
    },
    {
      question: "Can I insure a used Tata car?",
      answer:
        "Yes. Comprehensive and third-party insurance is available for used cars, subject to inspection.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation",
      description:
        "Great for Nexon, Safari and Harrier owners to avoid deductions on plastic or fiber parts.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Engine and Gearbox Protection",
      description:
        "Essential if you live in flood-prone or pothole-heavy areas.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Return to Invoice (RTI)",
      description: "Suitable for new buyers of Punch, Nexon and Safari.",
      icon: <FaFileInvoiceDollar className="text-4xl text-orange-600 mb-4" />,
    },
    {
      title: "Roadside Assistance",
      description: "Ideal for highway travelers or long-distance commuters.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Tyre and Rim Cover",
      description: "Valuable for SUVs with alloy wheels and low-profile tyres.",
      icon: <FaCarSide className="text-4xl text-green-600 mb-4" />,
    },
  ];

  const features = [
    { icon: "🛡️", text: "Reliable & Accurate Estimates" },
    { icon: "⏱️", text: "Takes Less Than a Minute" },
    { icon: "📉", text: "Instantly Shows Premium Breakup" },
    { icon: "📄", text: "No Paperwork or Documentation" },
    { icon: "📲", text: "Easy to Use on Any Device" },
    { icon: "💰", text: "Helps You Save on Insurance" },
  ];
const tatainsurance = [
    {
      name: 'Nexon',
      path: '/tata-nexon-car-insurance',
      image: '/assets/tatanexon.jpeg',
    },
     {
      name: 'Punch',
      path: '/tata-punch-car-insurance',
      image: '/assets/tatapunch.jpeg',
    },
      {
      name: 'Harrier',
      path: '/tata-harrier-car-insurance',
      image: '/assets/tataharrier.jpeg',
    },
      {
      name: 'Safari',
      path: '/tata-safari-car-insurance',
      image: '/assets/tatasafari.jpeg',
    },
      {
      name: 'Altroz',
      path: '/tata-altroz-car-insurance',
      image: '/assets/tataaltroz.jpeg',
    },
      {
      name: 'Tiago',
      path: '/tata-tiago-car-insurance',
      image: '/assets/tatatiago.jpeg',
    },
     {
      name: 'Tigor',
      path: '/tata-tigor-car-insurance',
      image: '/assets/tatatigor.jpeg',
    },
    {
      name: 'Curvv',
      path: '/tata-curvv-car-insurance',
      image: '/assets/tatacurvv.jpeg',
    },
  ];
  return (
    <>
      <Helmet>
        <title>Tata Car Insurance in India – Compare Quotes & Buy Online</title>
        <meta
          name="description"
          content="Explore Tata car insurance options in India. Compare quotes, customize coverage, and buy online to protect your Tata vehicle."
        />
        <meta
          name="keywords"
          content="tata car insurance, auto insurance india, tata nexon insurance, tata safari insurance, car insurance quotes"
        />
          <link rel="canonical" href="https://thefinmaster.com/tata-car-insurance" />
       <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
       
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">Tata Car Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">
                    Tata Motors Car Insurance
                  </span>{" "}
               
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                TATA Motors has been a trusted name on Indian roads, offering everything from compact hatchbacks to rugged SUVs. Whether you own a Tiago or a Harrier, securing your car with the right insurance is essential. This guide walks you through everything you need to know about insuring a TATA Motors vehicle, including average premiums, model-specific insights, useful add-ons, renewal tips, and frequently asked questions.
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/tatainsurance.jpeg"
                alt="Tata Car"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center p-4 mt-6">
        <div className="bg-white shadow-lg rounded-2xl p-6 max-w-7xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            About Tata Motors in India
          </h2>
          <p className="text-gray-600 leading-relaxed">
            Tata Motors is one of India’s most respected automotive
            manufacturers and a pioneer in developing vehicles designed for
            Indian roads and preferences. Known for their strong safety ratings
            and practical design, Tata cars appeal to both first-time buyers and
            experienced car owners.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            The brand offers hatchbacks, sedans, EVs and a wide range of SUVs.
            With popular models like the Nexon, Punch and Harrier gaining rapid
            popularity, it's important to choose the right insurance coverage
            that matches the strength and technology of Tata vehicles.
          </p>
        </div>
      </div>
         <div className="p-4 sm:p-8 bg-white">
                  <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
                    Choose Your Car Brand
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {tatainsurance.map((calc, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.04 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <Link
                    to={calc.path}
                    className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
                  >
                    <img
                      src={calc.image}
                      alt={calc.name}
                      className="w-16 h-16 object-contain mb-2"
                    />
                    <span className="text-md font-medium text-gray-800 break-words leading-snug">
                      {calc.name}
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
            </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why Tata Owners Need a Thoughtful Insurance Plan
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            Tata cars are built tough and come with features that enhance
            driving comfort and safety. That said, accidents, theft or natural
            calamities can still lead to expensive repairs. A good insurance
            policy will help protect you from:
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>On-road accidents or damage caused by collisions</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>Theft and vandalism</span>
            </li>
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>Fire, floods, landslides and other natural disasters</span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>Third-party injury or property damage liabilities</span>
            </li>
            <li className="flex items-start gap-3">
              <FaTools className="text-xl text-indigo-500 mt-1" />
              <span>Rising costs of spare parts and repairs</span>
            </li>
          </ul>
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            A reliable policy also gives you access to Tata-authorized garages,
            cashless claim options and tailored add-ons for your specific model.
          </div>
        </motion.div>
      </section>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your Car Insurance <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your premium — no
              paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/car-insurance-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl" role="img" aria-label={item.text}>
                  {item.icon}
                </span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Popular Tata Models and Insurance Cost Estimates
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Here’s an overview of Tata’s best-selling models along with their
          approximate IDV range and starting premiums for comprehensive
          coverage:
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              {
                model: "Tiago",
                category: "Hatchback",
                idv: "5–7 Lakh",
                premium: "₹2,400 to ₹4,000",
              },
              {
                model: "Tigor",
                category: "Compact Sedan",
                idv: "6–8 Lakh",
                premium: "₹2,800 to ₹4,500",
              },
              {
                model: "Punch",
                category: "Micro SUV",
                idv: "7–9 Lakh",
                premium: "₹3,000 to ₹4,800",
              },
              {
                model: "Nexon",
                category: "Compact SUV",
                idv: "9–14 Lakh",
                premium: "₹3,500 to ₹6,500",
              },
              {
                model: "Altroz",
                category: "Premium Hatch",
                idv: "7–10 Lakh",
                premium: "₹3,200 to ₹5,200",
              },
              {
                model: "Harrier",
                category: "Mid-size SUV",
                idv: "15–21 Lakh",
                premium: "₹4,800 to ₹7,900",
              },
              {
                model: "Safari",
                category: "Full-size SUV",
                idv: "17–24 Lakh",
                premium: "₹5,000 to ₹8,400",
              },
              {
                model: "Nexon EV",
                category: "Electric SUV",
                idv: "14–18 Lakh",
                premium: "₹4,600 to ₹7,500",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="border mb-4 p-4 rounded-lg bg-white shadow-md"
              >
                <h3 className="font-semibold text-gray-800 mb-2">
                  {item.model}
                </h3>
                <div className="text-gray-600 mb-1">
                  <strong>Category:</strong> {item.category}
                </div>
                <div className="text-gray-600 mb-1">
                  <strong>Approx. IDV:</strong> {item.idv}
                </div>
                <div className="text-gray-600">
                  <strong>Starting Premium:</strong> {item.premium}
                </div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">
                    Model
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Category
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Approx. IDV (₹)
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Starting Premium (₹)
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["Tiago", "Hatchback", "5–7 Lakh", "₹2,400 to ₹4,000"],
                  ["Tigor", "Compact Sedan", "6–8 Lakh", "₹2,800 to ₹4,500"],
                  ["Punch", "Micro SUV", "7–9 Lakh", "₹3,000 to ₹4,800"],
                  ["Nexon", "Compact SUV", "9–14 Lakh", "₹3,500 to ₹6,500"],
                  ["Altroz", "Premium Hatch", "7–10 Lakh", "₹3,200 to ₹5,200"],
                  ["Harrier", "Mid-size SUV", "15–21 Lakh", "₹4,800 to ₹7,900"],
                  ["Safari", "Full-size SUV", "17–24 Lakh", "₹5,000 to ₹8,400"],
                  [
                    "Nexon EV",
                    "Electric SUV",
                    "14–18 Lakh",
                    "₹4,600 to ₹7,500",
                  ],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Add-Ons That Make Sense for Tata Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Tata vehicles are known for robust bodies and safety features. To
          further protect your car, consider these popular add-on covers:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Best Insurance Companies for Tata Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Several insurers offer tailored plans and cashless coverage at
          Tata-authorized garages. Some of the best providers include:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📋</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Tata AIG
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Offers specialized Tata car policies and fast claims.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              HDFC ERGO
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Known for digital claims and wide garage reach.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              ICICI Lombard
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Great for urban users with Harrier or Nexon.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Reliance General
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Budget-friendly with wide add-on options.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              SBI General
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Ideal for first-time car owners and lower variants.
            </p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                How to Save on Tata Car Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Here are some easy ways to reduce the insurance premium for your
              Tata vehicle while keeping the coverage intact:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Compare online quotes before policy purchase or renewal.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Maintain a claim-free record to avail No Claim Bonus.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Opt for voluntary deductibles if you are confident in your
                  driving habits.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Install certified anti-theft systems or gear locks.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Avoid small claims to preserve discounts for later years.
                </span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for Tata Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              For buying or renewing Tata car insurance, keep these documents
              ready:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Car Registration Certificate (RC)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Existing policy (if applicable)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Identity proof (PAN or Aadhaar)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Pollution Under Control (PUC) certificate</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl sm:col-span-2">
                <span>
                  Passport-size photographs (for offline processing, if
                  required)
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          Tata Car Insurance FAQs
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <FaCarSide />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Tata cars offer an excellent blend of safety, design and driving
                comfort. Selecting the right insurance plan ensures that you are
                protected from both financial losses and legal liabilities.
                Whether you drive a Tiago or a Safari, a strong policy with the
                right add-ons can make a big difference when it counts the most.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                At TheFinMaster, we help you explore top plans, compare premiums
                and choose smart insurance options tailored for your Tata
                vehicle.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default TataCarInsurance;
